import api from './api';
import { connectSSE, type SSEEventHandler } from './v2-chat-api';
import type { ChatMessage } from '@/types/chat';

export type SemanticEntityType =
  | 'metric'
  | 'segment'
  | 'time_window'
  | 'glossary'
  | 'product_mapping';

export interface SemanticsItem {
  id: string;
  type: SemanticEntityType;
  key: string;
  name: string;
  organizationId: string;
  orgName: string;
  accountName: string | null;
  sourceId: string | null;
  sourceName: string | null;
  confirmedDefinition: Record<string, unknown> | null;
  proposedVersionId: string | null;
  proposedDefinition: Record<string, unknown> | null;
  proposedNote: string | null;
}

interface RawRow {
  id: string;
  type: SemanticEntityType;
  key: string;
  name: string;
  organization_id: string;
  org_name: string;
  account_name: string | null;
  source_id: string | null;
  source_name: string | null;
  confirmed_definition: Record<string, unknown> | null;
  proposed_version_id: string | null;
  proposed_definition: Record<string, unknown> | null;
  proposed_note: string | null;
}

export type SemanticVersionStatus = 'proposed' | 'confirmed' | 'superseded' | 'discarded';

/** One definition for the detail pane — confirmed live version + any pending proposal. */
export interface SemanticEntityView {
  id: string;
  type: SemanticEntityType;
  key: string;
  name: string;
  organizationId: string;
  orgName: string;
  dataSourceAccountId: string | null;
  accountName: string | null;
  sourceId: string | null;
  sourceName: string | null;
  confirmedDefinition: Record<string, unknown> | null;
  confirmedVersionNumber: number | null;
  confirmedValidFrom: string | null;
  proposedVersionId: string | null;
  proposedDefinition: Record<string, unknown> | null;
  proposedNote: string | null;
  proposedVersionNumber: number | null;
  updatedAt: string;
  canManage: boolean;
  canConfirm: boolean;
}

export interface SemanticVersionEntry {
  id: string;
  versionNumber: number;
  status: SemanticVersionStatus;
  definition: Record<string, unknown> | null;
  changeNote: string | null;
  actorType: 'user' | 'llm' | 'system';
  actorName: string;
  createdAt: string;
  validFrom: string | null;
  validTo: string | null;
}

export interface SemanticVersionList {
  currentVersionId: string | null;
  versions: SemanticVersionEntry[];
}

export interface SemanticDiscussState {
  threadId: string | null;
  messages: ChatMessage[];
}

export const semanticsApi = {
  getOverview: async (): Promise<SemanticsItem[]> => {
    const { data } = await api.get('/api/semantics/overview');
    return (data.data as RawRow[]).map((r) => ({
      id: r.id,
      type: r.type,
      key: r.key,
      name: r.name,
      organizationId: r.organization_id,
      orgName: r.org_name,
      accountName: r.account_name,
      sourceId: r.source_id,
      sourceName: r.source_name,
      confirmedDefinition: r.confirmed_definition,
      proposedVersionId: r.proposed_version_id,
      proposedDefinition: r.proposed_definition,
      proposedNote: r.proposed_note,
    }));
  },

  getEntity: async (id: string): Promise<SemanticEntityView> => {
    const { data } = await api.get(`/api/semantics/entities/${id}`);
    return data.data as SemanticEntityView;
  },

  /** Dry-run a candidate definition against an existing entity's account. */
  preview: async (
    id: string,
    definition: Record<string, unknown>,
  ): Promise<Record<string, unknown>> => {
    const { data } = await api.post(`/api/semantics/entities/${id}/preview`, { definition });
    return data.data as Record<string, unknown>;
  },

  /** Human manual edit → goes live immediately (update + confirm). */
  saveVersion: async (
    id: string,
    body: { definition: Record<string, unknown>; name?: string; change_note?: string },
  ): Promise<void> => {
    await api.post(`/api/semantics/entities/${id}/versions`, body);
  },

  /** Human manual create (blank-JSON path) → goes live immediately. */
  create: async (body: {
    type: SemanticEntityType;
    key: string;
    name: string;
    definition: Record<string, unknown>;
    data_source_account_id?: string;
    change_note?: string;
  }): Promise<{ entityId: string }> => {
    const { data } = await api.post('/api/semantics/entities', body);
    return data.data as { entityId: string };
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/api/semantics/entities/${id}`);
  },

  getVersions: async (id: string): Promise<SemanticVersionList> => {
    const { data } = await api.get(`/api/semantics/entities/${id}/versions`);
    return data.data as SemanticVersionList;
  },

  getDiscuss: async (entityId: string): Promise<SemanticDiscussState> => {
    const { data } = await api.get(`/api/semantics/entities/${entityId}/discuss`);
    return data.data as SemanticDiscussState;
  },

  /** Streaming Discuss turn on an existing definition — SSE incl. version_proposed. */
  discuss: (
    entityId: string,
    params: { message: string },
    onEvent: SSEEventHandler,
    signal?: AbortSignal,
  ): Promise<void> => connectSSE(`/api/semantics/entities/${entityId}/discuss`, params, onEvent, signal),

  /** Streaming Discuss turn to draft a NEW definition (no entity yet). The org
   *  is chosen explicitly so the agent sees the right business's data sources. */
  discussNew: (
    params: { organization_id: string; message: string; data_source_account_id?: string; threadId?: string },
    onEvent: SSEEventHandler,
    signal?: AbortSignal,
  ): Promise<void> => connectSSE('/api/semantics/discuss/new', params, onEvent, signal),

  confirmVersion: async (versionId: string): Promise<void> => {
    await api.post(`/api/semantics/versions/${versionId}/confirm`);
  },

  discardVersion: async (versionId: string): Promise<void> => {
    await api.post(`/api/semantics/versions/${versionId}/discard`);
  },
};
